import * as actionType from "./actionTypes";
import { getRequestAuthed, postRequestAuthed, processError, prepPostData } from "../../utils/apiHandlers";
import { LOADING_STATE, LS_ITEM_ADMIN, NOTIF_TYPE } from "../../utils/stateTypes";
import { deleteItemLocal, editItemProp, setItemEditing, updateItemLocal } from "../../utils/actionUtils";
import { adminProductSelectors } from "../rootSelectors";
import _notif from "../notification";


//collectionFilter is null for "all products", a collection ID for one collection,
//or COLLECTION_FILTER.UNASSIGNED for products not yet sorted into a collection.
export const COLLECTION_FILTER = {
  ALL: null,
  UNASSIGNED: "unassigned",
};

const buildCollectionQuery = (collectionFilter) => {
  if (collectionFilter === COLLECTION_FILTER.UNASSIGNED) {
    return "&unassignedOnly=true";
  }
  if ((collectionFilter !== null) && (collectionFilter !== undefined)) {
    return `&collectionID=${collectionFilter}`;
  }
  return "";
};

export const loadProducts = (categoryID, page=0, pageSize=25, collectionFilter=null) => (
  (dispatch) => {
    const lsItem = LS_ITEM_ADMIN.PRODUCTS_LOAD;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    getRequestAuthed(`/AdminProduct/GetProducts/${categoryID}?page=${page}&pageSize=${pageSize}${buildCollectionQuery(collectionFilter)}`,
      (pagedProducts) => {
        const formattedProduct = {
          byID: {},
          allIDs: [],
        };
        
        for (let product of pagedProducts.products) {
          formattedProduct.byID[product.id] = product;
          formattedProduct.allIDs.push(product.id);
        }

        dispatch({
          type: actionType.RECEIVE_PRODUCTS,
          product: formattedProduct,
          loadedCategoryID: categoryID,
          collectionFilter,
          page,
          pageSize,
          totalProducts: pagedProducts.totalCount,
        });
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const setProductEditing = setItemEditing(actionType.SET_PRODUCT_EDITING);
export const editProductProp = editItemProp(actionType.EDIT_PRODUCT_PROP);
const updateProductLocal = updateItemLocal(actionType.UPDATE_PRODUCT, "product");
const deleteProductLocal = deleteItemLocal(actionType.DELETE_PRODUCT);

export const addProduct = (categoryID, collectionID=null) => (
  (dispatch) => {
    let lsItem = LS_ITEM_ADMIN.PRODUCT_ADD;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed("/AdminProduct/AddProduct",
      {
        categoryID,
        collectionID,
      },
      (product) => {
        dispatch(updateProductLocal(product, true));
        dispatch(_notif.actions.queueNotification("Product added", NOTIF_TYPE.SUCCESS));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const saveProduct = () => (
  (dispatch, getState) => {
    const product = adminProductSelectors.getProductEditing(getState());

    let postData = prepPostData(product, ["previewImage", "previewVideo"]);

    //The "no collection" option comes back as an empty string, which will not bind to a nullable int
    if (postData.item.collectionID === "") {
      postData.item.collectionID = null;
    }

    //Separate the info section files
    let extraFiles = [];
    if (product.images) {
      let newImages = [];
      for (let item of product.images) {
        if (!item.image) {
          continue;
        }
        const processedItem = prepPostData(item, ["image"]);
        newImages.push(processedItem.item);
        if (processedItem.newFiles.length > 0) {
          processedItem.newFiles[0].propName = item.id.toString();
        }
        extraFiles = [
          ...extraFiles,
          ...processedItem.newFiles,
        ];
      }
      postData.item.images = newImages;
    }

    //Give an order prop to the InfoSections
    if (postData.item.infoSections) {
      postData.item.infoSections.forEach((item, index) => {
        item.order = index;
      });
    }

    let lsItem = LS_ITEM_ADMIN.PRODUCT_SAVE;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed("/AdminProduct/SaveProduct",
      {
        item: postData.item,
        newFiles: postData.newFiles,
        extraFiles,
      },
      (product) => {
        dispatch(updateProductLocal(product));
        dispatch(setProductEditing(product.id));

        dispatch(_notif.actions.queueNotification("Product updated", NOTIF_TYPE.SUCCESS));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const deleteProduct = (id, onSuccess=null) => (
  (dispatch) => {
    let lsItem = `${LS_ITEM_ADMIN.PRODUCT_DELETE}${id}`;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed("/AdminProduct/DeleteProduct",
      id,
      () => {
        dispatch(deleteProductLocal(id));

        dispatch(_notif.actions.queueNotification("Product deleted", NOTIF_TYPE.SUCCESS));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        if (onSuccess) {
          onSuccess();
        }
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const reorderProducts = (startIndex, endIndex, categoryID, page=0, pageSize=25, collectionFilter=null) => (
  (dispatch, getState) => {
    const itemID = adminProductSelectors.getProducts(getState()).find((item) => item.order === startIndex)?.id;

    //Ordering only applies within a single collection, so the unassigned/all views cannot reorder
    const collectionID = ((collectionFilter === COLLECTION_FILTER.UNASSIGNED) || (collectionFilter === null) || (collectionFilter === undefined)) ? null : collectionFilter;

    const lsItem = `${LS_ITEM_ADMIN.PRODUCT_REORDER}${itemID}`;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed("/AdminProduct/ReorderProducts",
      {
        startIndex,
        endIndex,
        parentID: categoryID,
        collectionID,
      },
      () => {
        dispatch(_notif.actions.queueNotification("Re-ordered successfully", NOTIF_TYPE.SUCCESS));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(loadProducts(categoryID, page, pageSize, collectionFilter));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const assignProductsToCollection = (productIDs, collectionID, categoryID, page=0, pageSize=25, collectionFilter=null) => (
  (dispatch) => {
    const lsItem = LS_ITEM_ADMIN.PRODUCT_ASSIGN;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed("/AdminProduct/AssignProductsToCollection",
      {
        productIDs,
        collectionID,
      },
      () => {
        dispatch(_notif.actions.queueNotification(
          (collectionID === null) ? "Products removed from their collection" : "Products moved to the collection",
          NOTIF_TYPE.SUCCESS,
        ));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(loadProducts(categoryID, page, pageSize, collectionFilter));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const loadProduct = (productID) => (
  (dispatch) => {
    const lsItem = LS_ITEM_ADMIN.PRODUCTS_LOAD;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    getRequestAuthed(`/AdminProduct/GetProduct/${productID}`,
      (product) => {
        dispatch({ type: actionType.RECEIVE_PRODUCT, product });
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);
